import { FC, useState } from "react";
import { Link } from "react-router-dom";
import { useDebounce } from "use-debounce";
import {
  Anchor,
  Button,
  Divider,
  Flex,
  Group,
  Pagination,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
  useMatches,
} from "@mantine/core";
import {
  IconCircleDot,
  IconRotate,
  IconSearch,
  IconUser,
} from "@tabler/icons-react";

import { useOrdersList, useOrdersListSummary } from "@jasper/use-cases";
import { OrdersSummaryTable } from "/@/components/order/OrdersSummaryTable";
import { OrderStatusV2 } from "/@/generated/graphql";
import { FilterDropdown } from "/@/pages/orders/FilterDropdown";
import { ORDER_STATUS_V2_FILTER_OPTIONS } from "/@/pages/orders/orderStatusV2";
import { useAuthContext } from "/@/shared/contexts/AuthContext";

const PAGE_SIZE = 6;

const STATUS_OPTIONS = [
  { value: "", label: "Tous" },
  ...ORDER_STATUS_V2_FILTER_OPTIONS,
];

type OrdersListFilters = {
  search: string;
  dentistId: string | null;
  status: OrderStatusV2 | null;
};

const NO_FILTERS: OrdersListFilters = {
  search: "",
  dentistId: null,
  status: null,
};

export const OrdersListPageV2: FC = () => {
  const { user } = useAuthContext();
  // Without a dental office, the dentist gets their own orders.
  const dentalOfficeId = user?.userGroup?.id ?? null;

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [debouncedSearch] = useDebounce(filters.search, 500);

  const hasActiveFilters =
    !!filters.search.trim() || !!filters.dentistId || !!filters.status;
  const filter = {
    search: debouncedSearch.trim() || undefined,
    dentistId: filters.dentistId ?? undefined,
    status: filters.status ? { included: [filters.status] } : undefined,
  };

  const updateFilters = (changes: Partial<OrdersListFilters>) => {
    setPage(1);
    setFilters(current => ({ ...current, ...changes }));
  };

  const {
    orders,
    materialAndShadeCatalog,
    totalCount: filteredCount = 0,
    isPreviousPage,
    loading,
    error,
  } = useOrdersList({
    dentalOfficeId,
    filter,
    pageSize: PAGE_SIZE,
    page,
    withTotalCount: true,
    skip: !user,
  });

  const summary = useOrdersListSummary({ dentalOfficeId, skip: !user });

  const toolbarRadius = useMatches({ base: "lg", lg: "full" });

  if (summary?.total === 0) {
    return (
      <Stack gap="s1" justify="center" align="center" h="100%" p="md">
        <Title order={3}>{"Vous n'avez pas encore passé commande"}</Title>
        <Text variant="caption">
          Veuillez passer une commande via votre caméra intra-orale
        </Text>
        <Text variant="caption">
          {"ou via le formulaire de commande en haut à droite de l'écran"}
        </Text>
      </Stack>
    );
  }

  const dentistOptions = [
    { value: "", label: "Tout le cabinet" },
    ...(user?.userGroup?.groupMembers ?? [])
      .filter(dentist => !dentist.isManager)
      .map(dentist => ({
        value: dentist.id,
        label: `Dr. ${dentist.firstName} ${dentist.lastName}`,
      })),
  ];

  const emptyTableMessage = loading
    ? "Chargement…"
    : error
      ? "Impossible de charger les commandes."
      : "Aucune commande ne correspond à ces filtres.";

  const pageCount = Math.ceil(filteredCount / PAGE_SIZE);
  const firstDisplayed = PAGE_SIZE * (page - 1) + 1;
  const lastDisplayed = firstDisplayed + orders.length - 1;

  return (
    <Stack gap="lg" p="md">
      <Stack gap={2}>
        <Title variant="display" size="lg">
          Mes{" "}
          <Text span inherit variant="accent">
            commandes
          </Text>
          .
        </Title>
        <Text variant="caption">
          <Text span fw={600} inherit>
            {summary?.total ?? "–"}
          </Text>{" "}
          commandes ·{" "}
          {!!summary?.awaitingAction && (
            <>
              <Text span fw={600} inherit>
                {summary.awaitingAction}
              </Text>{" "}
              {summary.awaitingAction > 1 ? "attendent" : "attend"} une action
              ·{" "}
            </>
          )}
          <Anchor component={Link} to="/" inherit fw={600} c="brand.6">
            chiffres et notifications sur le tableau de bord
          </Anchor>
        </Text>
      </Stack>

      <Paper
        radius={toolbarRadius}
        bg="cardBackground"
        withBorder
        shadow="xs"
        py="s1"
        pl="s3"
        pr="s2"
      >
        <Flex gap="s2" align="center" wrap={{ base: "wrap", lg: "nowrap" }}>
          <TextInput
            flex={{ base: "1 1 100%", lg: 1 }}
            variant="unstyled"
            placeholder="Référence ou nom du patient"
            leftSection={<IconSearch size={16} />}
            value={filters.search}
            onChange={event =>
              updateFilters({ search: event.currentTarget.value })
            }
          />
          <Divider orientation="vertical" color="ink.1" visibleFrom="lg" />
          <FilterDropdown
            icon={<IconCircleDot size={14} />}
            label="Statut"
            options={STATUS_OPTIONS}
            value={filters.status}
            onChange={value =>
              updateFilters({ status: value as OrderStatusV2 | null })
            }
          />
          {dentalOfficeId && (
            <FilterDropdown
              icon={<IconUser size={14} />}
              label="Praticien"
              options={dentistOptions}
              value={filters.dentistId}
              onChange={dentistId => updateFilters({ dentistId })}
              searchPlaceholder="Rechercher un praticien"
            />
          )}
          {hasActiveFilters && (
            <Button
              variant="subtle"
              color="ink"
              leftSection={<IconRotate size={14} />}
              onClick={() => updateFilters(NO_FILTERS)}
            >
              Réinitialiser
            </Button>
          )}
        </Flex>
      </Paper>

      <OrdersSummaryTable
        orders={orders}
        materialAndShadeCatalog={materialAndShadeCatalog}
        emptyMessage={emptyTableMessage}
        isDimmed={isPreviousPage}
        footer={
          pageCount > 1 &&
          orders.length > 0 && (
            <>
              <Divider color="ink.1" />
              <Group justify="space-between" gap="s4" px="s4" py="s2">
                <Text variant="caption">
                  {`${firstDisplayed}–${lastDisplayed} sur ${filteredCount} commandes`}
                </Text>
                <Pagination total={pageCount} value={page} onChange={setPage} />
              </Group>
            </>
          )
        }
      />
    </Stack>
  );
};
